import { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import UiIcon from '../components/UiIcon';
import { buildCrmSwalBody, fireConfirmDialog } from '../utils/swalConfirm';
import { API_BASE_URL } from '../config/env';
import { useAuth } from '../context/AuthContext';
import '../styles/admin-roles-permisos-premium.css';

const PERMISSION_LABELS = {
  ver_contactos: 'Contactos',
  ver_trazabilidad_asignaciones: 'Trazabilidad asignaciones (Chatwoot)',
  ver_conversaciones: 'Conversaciones',
  ver_pedido_recetas_medico: 'Pedidos/Recetas',
  ver_pedidos: 'Última milla',
  ver_pedidos_dispensa: 'Contact Center',
  ver_pulse_ledger: 'Trazabilidad',
  ver_trazabilidad_recetas: 'Trazabilidad recetas (ciclo completo)',
  ver_recetas_cruzadas: 'Recetas cruzadas',
  ver_recetas_emitir: 'Recetas a emitir',
  manual_conversaciones: 'Manual de conversaciones',
  ver_gestion_derivacion: 'Gestión Derivación',
  admin_medicos_visibilidad: 'Médicos en derivación (visibilidad)',
  admin_administrativos_visibilidad: 'Administrativos en emisión (visibilidad)',
  admin_callcenter_visibilidad: 'Callcenter en emisión (visibilidad)',
  admin_sucursales_farmacia: 'Sucursales de Farmacia (CRUD)',
  admin_poligonos_farmacia: 'Polígonos de Farmacia (CRUD)',
  admin_horarios_botrecetasatencion: 'Horarios de BotRecetas (CRUD)',
  ver_manual_administrativo: 'Manual administrativo (Derivación y Recetas)',
  ver_manual_medico: 'Manual médico (Pedidos/Recetas)',
  crear_pedido_alta_rapida: 'Alta rápida de pedido',
  ver_bot_farmacia: 'Bot Farmacia',
  ver_encuesta_bot: 'Encuesta bot (generador)',
  ver_encuesta_bot_review: 'Review encuestas bot',
  eliminar_conversaciones: 'Eliminar conversaciones (Chatwoot)',
  asignar_agente_conversacion: 'Asignar operador a conversaciones (Chatwoot)',
  ver_directorio: 'Directorio unificado (Pedidos · Aprobación · Emisión)',
  ver_metodo_pago: 'Método de pago',
};

const PERMISSION_HINTS = {
  ver_directorio: 'Vista CRM unificada del flujo de recetas',
  ver_gestion_derivacion: 'Paso 1 · Pedidos pendientes de derivación',
  ver_pedido_recetas_medico: 'Paso 2 · Aprobación de tratamientos',
  ver_recetas_emitir: 'Paso 3 · Recetas listas para emitir',
  ver_recetas_cruzadas: 'Cruce de recetas entre sistemas',
  crear_pedido_alta_rapida: 'Alta rápida por DNI + medicamento',
  ver_trazabilidad_recetas: 'Historial completo del ciclo de receta',
  ver_pedidos_dispensa: 'Bandeja Contact Center / dispensa',
  ver_pedidos: 'Seguimiento de pedidos en última milla',
  ver_contactos: 'Directorio de contactos y clientes',
  ver_conversaciones: 'Bandeja de conversaciones Chatwoot',
  eliminar_conversaciones: 'Permite borrar conversaciones en Chatwoot',
  asignar_agente_conversacion: 'Reasignar operador en Chatwoot',
  ver_trazabilidad_asignaciones: 'Auditoría de asignaciones Chatwoot',
  ver_pulse_ledger: 'Ledger operativo Pulse',
  ver_bot_farmacia: 'Simulador Bot Farmacia',
  ver_encuesta_bot: 'Generador de encuestas del bot',
  ver_encuesta_bot_review: 'Revisión de respuestas de encuestas',
  ver_manual_administrativo: 'Manual para rol administrativo',
  ver_manual_medico: 'Manual para rol médico',
  manual_conversaciones: 'Manual de conversaciones CRM',
  admin_medicos_visibilidad: 'Quién aparece en derivación',
  admin_administrativos_visibilidad: 'Quién aparece en emisión',
  admin_callcenter_visibilidad: 'Quién aparece en emisión Call Center',
  admin_sucursales_farmacia: 'Alta, edición, eliminación y mapa de sucursales',
  admin_poligonos_farmacia: 'Alta, edición, eliminación y dibujo de zonas de cobertura',
  admin_horarios_botrecetasatencion: 'Alta, edición, eliminación y activación de horarios del chat BotRecetas',
  ver_metodo_pago: 'Permite ver el método de pago en la bandeja de pedidos',
};

const PERMISSION_GROUPS = [
  {
    key: 'recetas',
    title: 'Directorio y recetas',
    icon: '📋',
    tone: 'blue',
    codes: [
      'ver_directorio',
      'ver_gestion_derivacion',
      'ver_pedido_recetas_medico',
      'ver_recetas_emitir',
      'ver_recetas_cruzadas',
      'crear_pedido_alta_rapida',
      'ver_trazabilidad_recetas'
    ]
  },
  {
    key: 'operaciones',
    title: 'Operaciones',
    icon: '🏍️',
    tone: 'cyan',
    codes: ['ver_pedidos_dispensa', 'ver_pedidos']
  },
  {
    key: 'contacto',
    title: 'Contacto y Chatwoot',
    icon: '💬',
    tone: 'violet',
    codes: [
      'ver_contactos',
      'ver_conversaciones',
      'eliminar_conversaciones',
      'asignar_agente_conversacion',
      'ver_trazabilidad_asignaciones'
    ]
  },
  {
    key: 'trazabilidad',
    title: 'Trazabilidad',
    icon: '🧭',
    tone: 'indigo',
    codes: ['ver_pulse_ledger']
  },
  {
    key: 'bot',
    title: 'Bot y encuestas',
    icon: '🤖',
    tone: 'amber',
    codes: ['ver_bot_farmacia', 'ver_encuesta_bot', 'ver_encuesta_bot_review']
  },
  {
    key: 'manuales',
    title: 'Manuales',
    icon: '📚',
    tone: 'slate',
    codes: ['ver_manual_administrativo', 'ver_manual_medico', 'manual_conversaciones']
  },
  {
    key: 'admin',
    title: 'Visibilidad en flujos',
    icon: '⚙️',
    tone: 'rose',
    codes: [
      'admin_medicos_visibilidad',
      'admin_administrativos_visibilidad',
      'admin_callcenter_visibilidad',
      'admin_sucursales_farmacia',
      'admin_poligonos_farmacia',
      'admin_horarios_botrecetasatencion',
      'ver_metodo_pago',
      'ver_terminos_condiciones_grupo_bienestar',
      'ver_promociones_bot_farmacia',
      'ver_informacion_centro_regional',
      'ver_promociones_bancarias',
      'ver_delivery_config',
    ]
  }
];

function formatPermissionLabel(code) {
  const key = String(code || '').trim();
  if (!key) return '—';
  if (PERMISSION_LABELS[key]) return PERMISSION_LABELS[key];
  return key
    .replace(/^(ver_|admin_|eliminar_|asignar_)/i, '')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function permissionHint(code) {
  return PERMISSION_HINTS[String(code || '').trim()] || 'Control de acceso al módulo CRM';
}

function roleInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

function samePermIdSet(a = [], b = []) {
  const left = new Set((a || []).map(Number));
  const right = new Set((b || []).map(Number));
  if (left.size !== right.size) return false;
  for (const id of left) {
    if (!right.has(id)) return false;
  }
  return true;
}

function buildGroupedPermissions(allPermissions) {
  const byCode = new Map((allPermissions || []).map((p) => [String(p.code), p]));
  const usedCodes = new Set();
  const groups = PERMISSION_GROUPS.map((group) => {
    const items = group.codes
      .map((code) => byCode.get(code))
      .filter(Boolean);
    items.forEach((item) => usedCodes.add(String(item.code)));
    return { ...group, items };
  }).filter((group) => group.items.length > 0);

  const others = (allPermissions || []).filter((p) => !usedCodes.has(String(p.code)));
  if (others.length) {
    groups.push({
      key: 'otros',
      title: 'Otros permisos',
      icon: '🔐',
      tone: 'neutral',
      items: others
    });
  }
  return groups;
}

function matchesPermissionSearch(permission, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const label = formatPermissionLabel(permission?.code).toLowerCase();
  const code = String(permission?.code || '').toLowerCase();
  const hint = permissionHint(permission?.code).toLowerCase();
  return label.includes(q) || code.includes(q) || hint.includes(q);
}

function matchesPermissionFilter(permissionId, filter, selectedPermIds) {
  const active = selectedPermIds.includes(Number(permissionId));
  if (filter === 'active') return active;
  if (filter === 'inactive') return !active;
  return true;
}

function AdminRolesPermisos() {
  const { user, refreshPermissions } = useAuth();
  const isAdmin = Boolean(user?.isAdmin);

  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [selectedPermIds, setSelectedPermIds] = useState([]);
  const [newRoleName, setNewRoleName] = useState('');
  const [isCreateRoleModalOpen, setIsCreateRoleModalOpen] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [isDeletePasswordReadonly, setIsDeletePasswordReadonly] = useState(true);
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [expandedDetailPanel, setExpandedDetailPanel] = useState(null);
  const [permSearch, setPermSearch] = useState('');
  const [permFilter, setPermFilter] = useState('all');
  const [savedPermIds, setSavedPermIds] = useState([]);
  const [expandedPermGroupKey, setExpandedPermGroupKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const canCreateRole = String(newRoleName || '').trim().length > 0 && !saving;

  const sortedRoles = useMemo(
    () =>
      [...roles].sort((a, b) =>
        String(a?.name || '').localeCompare(String(b?.name || ''), 'es', { sensitivity: 'base' })
      ),
    [roles]
  );

  const sortedPermissions = useMemo(
    () =>
      [...permissions].sort((a, b) =>
        String(formatPermissionLabel(a.code)).localeCompare(
          String(formatPermissionLabel(b.code)),
          'es',
          { sensitivity: 'base' }
        )
      ),
    [permissions]
  );

  const groupedPermissions = useMemo(
    () => buildGroupedPermissions(sortedPermissions),
    [sortedPermissions]
  );

  const hasUnsavedPermChanges = useMemo(
    () => !samePermIdSet(selectedPermIds, savedPermIds),
    [selectedPermIds, savedPermIds]
  );

  const activePermPct = useMemo(() => {
    if (!permissions.length) return 0;
    return Math.round((selectedPermIds.length / permissions.length) * 100);
  }, [permissions.length, selectedPermIds.length]);

  const selectedRole = useMemo(
    () => roles.find((r) => Number(r.id) === Number(selectedRoleId)) || null,
    [roles, selectedRoleId]
  );

  const roleStats = useMemo(
    () => ({
      roles: roles.length,
      permissions: permissions.length,
      activeOnRole: selectedPermIds.length
    }),
    [roles.length, permissions.length, selectedPermIds.length]
  );

  const showToast = (message, type = 'info') => {
    Swal.fire({
      toast: true,
      position: 'bottom-end',
      text: message,
      showConfirmButton: false,
      timer: 3200,
      timerProgressBar: true,
      icon: type,
      customClass: { popup: 'swal-toast-crm' },
      background: '#fff'
    });
  };

  const loadBase = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [rRes, pRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/roles`, { credentials: 'include' }),
        fetch(`${API_BASE_URL}/api/admin/permissions`, { credentials: 'include' })
      ]);
      const [rJson, pJson] = await Promise.all([rRes.json().catch(() => []), pRes.json().catch(() => [])]);
      if (!rRes.ok) throw new Error(rJson.error || 'No se pudieron cargar roles');
      if (!pRes.ok) throw new Error(pJson.error || 'No se pudieron cargar permisos');
      const roleList = Array.isArray(rJson) ? rJson : [];
      setRoles(roleList);
      setPermissions(Array.isArray(pJson) ? pJson : []);
    } catch (e) {
      setError(e.message || 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRolePermissions = useCallback(async (roleId) => {
    if (!roleId) {
      setSelectedPermIds([]);
      setSavedPermIds([]);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/roles/${roleId}/permissions`, { credentials: 'include' });
      const json = await res.json().catch(() => []);
      if (!res.ok) throw new Error(json.error || 'No se pudieron cargar permisos del rol');
      const ids = (Array.isArray(json) ? json : []).map((p) => Number(p.id));
      setSelectedPermIds(ids);
      setSavedPermIds(ids);
    } catch (e) {
      setError(e.message || 'No se pudieron cargar permisos del rol');
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    loadBase();
  }, [isAdmin, loadBase]);

  useEffect(() => {
    if (!isAdmin) return;
    loadRolePermissions(selectedRoleId);
  }, [selectedRoleId, isAdmin, loadRolePermissions]);

  useEffect(() => {
    if (!selectedRoleId) {
      setAdminPassword('');
      setShowAdminPassword(false);
      setIsDeletePasswordReadonly(true);
      setExpandedDetailPanel(null);
      setPermSearch('');
      setPermFilter('all');
      setExpandedPermGroupKey(null);
    }
  }, [selectedRoleId]);

  const togglePerm = (permId) => {
    setSelectedPermIds((prev) => {
      const next = new Set(prev);
      if (next.has(permId)) next.delete(permId);
      else next.add(permId);
      return Array.from(next);
    });
  };

  const setGroupPermissions = (permIds, enabled) => {
    setSelectedPermIds((prev) => {
      const next = new Set(prev);
      permIds.forEach((id) => {
        if (enabled) next.add(Number(id));
        else next.delete(Number(id));
      });
      return Array.from(next);
    });
  };

  const setAllPermissions = (enabled) => {
    if (enabled) {
      setSelectedPermIds(permissions.map((p) => Number(p.id)));
      return;
    }
    setSelectedPermIds([]);
  };

  const togglePermGroupAccordion = (groupKey) => {
    setExpandedPermGroupKey((current) => (current === groupKey ? null : groupKey));
  };

  const visibleGroupedPermissions = useMemo(() => (
    groupedPermissions
      .map((group) => {
        const items = group.items.filter(
          (p) => matchesPermissionSearch(p, permSearch)
            && matchesPermissionFilter(p.id, permFilter, selectedPermIds)
        );
        return { ...group, items };
      })
      .filter((group) => group.items.length > 0)
  ), [groupedPermissions, permSearch, permFilter, selectedPermIds]);

  useEffect(() => {
    if (!expandedPermGroupKey) return;
    if (!visibleGroupedPermissions.some((group) => group.key === expandedPermGroupKey)) {
      setExpandedPermGroupKey(null);
    }
  }, [visibleGroupedPermissions, expandedPermGroupKey]);

  const toggleDetailPanel = (panel) => {
    setExpandedDetailPanel((current) => (current === panel ? null : panel));
  };

  const savePermissions = async () => {
    if (!selectedRoleId) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/roles/${selectedRoleId}/permissions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ permissions: selectedPermIds })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'No se pudo guardar');
      await loadRolePermissions(selectedRoleId);
      setSavedPermIds(selectedPermIds.map(Number));
      if (refreshPermissions) await refreshPermissions();
      showToast('Permisos guardados. El menú se actualizó para tu sesión.', 'success');
    } catch (e) {
      const msg = e.message || 'No se pudo guardar';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const createRole = async () => {
    const name = String(newRoleName || '').trim();
    if (!name) return;

    setIsCreateRoleModalOpen(false);

    const safeName = name.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const confirm = await fireConfirmDialog({
      title: 'Crear rol',
      html: buildCrmSwalBody({
        lead: `Se creará el rol <strong>${safeName}</strong>.`,
        items: ['Podrás asignar permisos desde la lista']
      }),
      confirmButtonText: 'Crear rol',
      cancelButtonText: 'Cancelar'
    });
    if (!confirm.isConfirmed) {
      setIsCreateRoleModalOpen(true);
      return;
    }

    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/roles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'No se pudo crear el rol');
      setNewRoleName('');
      await loadBase();
      setSelectedRoleId(json.id);
      showToast('Rol creado correctamente.', 'success');
    } catch (e) {
      const msg = e.message || 'No se pudo crear el rol';
      setError(msg);
      showToast(msg, 'error');
      setIsCreateRoleModalOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const deleteRole = async () => {
    if (!selectedRoleId || !adminPassword) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/roles/${selectedRoleId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ adminPassword })
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || 'No se pudo eliminar el rol');
      }
      setAdminPassword('');
      const prevId = selectedRoleId;
      await loadBase();
      if (Number(prevId) === Number(selectedRoleId)) setSelectedRoleId(null);
      showToast('Rol eliminado correctamente.', 'success');
    } catch (e) {
      const msg = e.message || 'No se pudo eliminar el rol';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="crm-arp-page">
        <div className="crm-arp-alert crm-arp-alert--error" role="alert">
          No tenés permisos para administrar roles y permisos.
        </div>
      </div>
    );
  }

  return (
    <div className="crm-arp-page">
      <header className="crm-arp-page__head">
        <div className="crm-arp-page__head-main">
          <div className="crm-arp-page__icon" aria-hidden="true">
            <UiIcon name="shield" size={22} />
          </div>
          <div>
            <h1 className="crm-arp-page__title">Roles y permisos</h1>
            <p className="crm-arp-page__sub">
              Definí qué pantallas y acciones tiene cada rol. Los cambios aplican al menú según el rol del usuario.
            </p>
          </div>
        </div>
        <div className="crm-arp-page__head-actions">
          <div className="crm-arp-kpi-row" aria-label="Resumen">
            <span className="crm-arp-kpi crm-arp-kpi--accent">
              Roles <strong>{roleStats.roles}</strong>
            </span>
            <span className="crm-arp-kpi">
              Permisos <strong>{roleStats.permissions}</strong>
            </span>
            {selectedRole ? (
              <span className="crm-arp-kpi">
                Activos <strong>{roleStats.activeOnRole}</strong>
              </span>
            ) : null}
          </div>
          <button
            type="button"
            className="crm-profile-primary-btn crm-arp-btn-new"
            onClick={() => {
              setNewRoleName('');
              setIsCreateRoleModalOpen(true);
            }}
          >
            <UiIcon name="plus" size={16} />
            Nuevo rol
          </button>
        </div>
      </header>

      {error ? (
        <div className="crm-arp-alert crm-arp-alert--error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="crm-admin-rp-page-grid">
        <section className="crm-admin-rp-roles-panel">
          <div className="crm-arp-panel-head">
            <span className="crm-arp-panel-head__title">Roles disponibles</span>
            <span className="crm-arp-panel-head__meta">{roleStats.roles} en total</span>
          </div>
          <div className="crm-admin-rp-roles-list">
            {loading ? (
              <div className="crm-admin-rp-roles-list__state">
                <div className="crm-admin-rp-loading">
                  <span className="crm-admin-rp-loading-spinner" aria-hidden="true" />
                  <span>Cargando roles…</span>
                </div>
              </div>
            ) : null}
            {!loading && sortedRoles.length === 0 ? (
              <div className="crm-arp-empty">
                <div>
                  <strong>Sin roles</strong>
                  Creá el primer rol con el botón Nuevo rol.
                </div>
              </div>
            ) : null}
            {sortedRoles.map((role) => {
              const isSelected = Number(role.id) === Number(selectedRoleId);
              return (
                <button
                  key={role.id}
                  type="button"
                  className={`crm-admin-rp-role-row${isSelected ? ' is-selected' : ''}`}
                  onClick={() => setSelectedRoleId(role.id)}
                >
                  {isSelected ? <span className="crm-admin-rp-role-row__accent" aria-hidden="true" /> : null}
                  <span className="crm-admin-rp-role-row__name">{role.name}</span>
                  {isSelected ? (
                    <span className="crm-admin-rp-role-row__badge">Seleccionado</span>
                  ) : null}
                </button>
              );
            })}
          </div>

          {selectedRole ? (
            <div className="crm-admin-rp-roles-panel__foot">
              <div className={`crm-admin-rp-accordion${expandedDetailPanel === 'delete' ? ' is-open' : ''}`}>
                <button
                  type="button"
                  className="crm-admin-rp-accordion__trigger"
                  onClick={() => toggleDetailPanel('delete')}
                  aria-expanded={expandedDetailPanel === 'delete'}
                >
                  <span className="crm-admin-rp-accordion__title">Eliminar rol</span>
                  <span className="crm-admin-rp-accordion__hint">Requiere contraseña admin</span>
                  <span className="crm-admin-rp-accordion__chevron" aria-hidden="true">
                    {expandedDetailPanel === 'delete' ? '▾' : '▸'}
                  </span>
                </button>
                <div className={`crm-admin-rp-accordion__body${expandedDetailPanel === 'delete' ? ' is-open' : ''}`}>
                  <div className="crm-admin-rp-accordion__body-inner">
                    <div className="crm-admin-rp-delete-content">
                      <div className="crm-profile-input-wrap">
                        <input type="text" name="username" autoComplete="username" style={{ display: 'none' }} />
                        <input type="password" name="password" autoComplete="new-password" style={{ display: 'none' }} />
                        <span className="crm-profile-input-icon crm-profile-input-icon--svg" aria-hidden="true">
                          <UiIcon name="lock" size={16} />
                        </span>
                        <input
                          className="crm-profile-input"
                          type={showAdminPassword ? 'text' : 'password'}
                          placeholder="Contraseña admin"
                          value={adminPassword}
                          onChange={(e) => setAdminPassword(e.target.value)}
                          autoComplete="new-password"
                          name="delete-role-password"
                          autoCapitalize="none"
                          autoCorrect="off"
                          spellCheck={false}
                          readOnly={isDeletePasswordReadonly}
                          onFocus={() => setIsDeletePasswordReadonly(false)}
                        />
                        <button
                          type="button"
                          className="crm-profile-eye-btn"
                          onClick={() => setShowAdminPassword((prev) => !prev)}
                          aria-label={showAdminPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                          title={showAdminPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                        >
                          <UiIcon name={showAdminPassword ? 'eye-off' : 'eye'} size={16} />
                        </button>
                      </div>
                      <button
                        type="button"
                        className="crm-profile-logout-btn crm-admin-rp-delete-btn"
                        onClick={deleteRole}
                        disabled={!selectedRoleId || !adminPassword || saving}
                      >
                        Eliminar rol seleccionado
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </section>

        {selectedRole ? (
          <section className="crm-admin-rp-detail-panel crm-arp-perms-panel">
            <div className="crm-arp-perms-panel-top">
              <div className="crm-arp-perms-hero crm-arp-perms-hero--compact">
                <div className="crm-arp-perms-hero__avatar" aria-hidden="true">
                  {roleInitials(selectedRole.name)}
                </div>
                <div className="crm-arp-perms-hero__copy">
                  <div className="crm-arp-perms-hero__line">
                    <span className="crm-arp-perms-hero__eyebrow">Rol</span>
                    <h2 className="crm-arp-perms-hero__title">{selectedRole.name}</h2>
                    <span className="crm-arp-perms-hero__kpis">
                      <strong>{selectedPermIds.length}</strong>
                      <span>/{permissions.length}</span>
                      <span className="crm-arp-perms-hero__kpis-sep">·</span>
                      <strong>{activePermPct}%</strong>
                    </span>
                  </div>
                  <div className="crm-arp-perms-progress crm-arp-perms-progress--inline" aria-hidden="true">
                    <div className="crm-arp-perms-progress__bar" style={{ width: `${activePermPct}%` }} />
                  </div>
                </div>
              </div>

              <div className="crm-arp-perms-toolbar">
              <div className="crm-arp-perms-search-wrap">
                <span className="crm-arp-perms-search-icon" aria-hidden="true">
                  <UiIcon name="search" size={14} />
                </span>
                <input
                  className="crm-arp-perms-search"
                  type="search"
                  placeholder="Buscar permiso…"
                  value={permSearch}
                  onChange={(e) => setPermSearch(e.target.value)}
                />
                {permSearch ? (
                  <button
                    type="button"
                    className="crm-arp-perms-search-clear"
                    onClick={() => setPermSearch('')}
                    aria-label="Limpiar búsqueda"
                  >
                    ×
                  </button>
                ) : null}
              </div>
              <div className="crm-arp-perms-filters" role="tablist" aria-label="Filtrar permisos">
                {[
                  { key: 'all', label: 'Todos' },
                  { key: 'active', label: 'Activos' },
                  { key: 'inactive', label: 'Inactivos' }
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    role="tab"
                    aria-selected={permFilter === item.key}
                    className={`crm-arp-perms-filter${permFilter === item.key ? ' is-active' : ''}`}
                    onClick={() => setPermFilter(item.key)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <div className="crm-arp-perms-bulk">
                <button
                  type="button"
                  className="crm-arp-perms-bulk-btn"
                  onClick={() => setAllPermissions(true)}
                  disabled={saving || selectedPermIds.length === permissions.length}
                >
                  Activar
                </button>
                <button
                  type="button"
                  className="crm-arp-perms-bulk-btn crm-arp-perms-bulk-btn--ghost"
                  onClick={() => setAllPermissions(false)}
                  disabled={saving || selectedPermIds.length === 0}
                >
                  Desactivar
                </button>
              </div>
            </div>
            </div>

            <div className="crm-arp-perms-groups">
              {visibleGroupedPermissions.length === 0 ? (
                <div className="crm-arp-perms-empty">
                  <div className="crm-arp-perms-empty__icon" aria-hidden="true">🔍</div>
                  <strong>Sin coincidencias</strong>
                  <span>Probá otro término o cambiá el filtro.</span>
                </div>
              ) : null}
              {visibleGroupedPermissions.map((group) => {
                const groupIds = group.items.map((p) => Number(p.id));
                const activeInGroup = groupIds.filter((id) => selectedPermIds.includes(id)).length;
                const allActive = activeInGroup === groupIds.length && groupIds.length > 0;
                const isExpanded = expandedPermGroupKey === group.key;
                return (
                  <article
                    key={group.key}
                    className={`crm-arp-perm-group crm-arp-perm-group--${group.tone}${isExpanded ? ' is-expanded' : ''}`}
                  >
                    <header className="crm-arp-perm-group__head">
                      <button
                        type="button"
                        className="crm-arp-perm-group__toggle"
                        onClick={() => togglePermGroupAccordion(group.key)}
                        aria-expanded={isExpanded}
                      >
                        <span className="crm-arp-perm-group__icon" aria-hidden="true">{group.icon}</span>
                        <span className="crm-arp-perm-group__titles">
                          <span className="crm-arp-perm-group__title">{group.title}</span>
                          <span className="crm-arp-perm-group__meta">
                            {activeInGroup}/{group.items.length} activos
                          </span>
                        </span>
                        <span
                          className={`crm-arp-perm-group__chevron${isExpanded ? ' is-open' : ''}`}
                          aria-hidden="true"
                        />
                      </button>
                      <div className="crm-arp-perm-group__actions">
                        <button
                          type="button"
                          className="crm-arp-perm-group__action"
                          onClick={() => setGroupPermissions(groupIds, true)}
                          disabled={saving || allActive}
                        >
                          Todos
                        </button>
                        <button
                          type="button"
                          className="crm-arp-perm-group__action crm-arp-perm-group__action--ghost"
                          onClick={() => setGroupPermissions(groupIds, false)}
                          disabled={saving || activeInGroup === 0}
                        >
                          Ninguno
                        </button>
                      </div>
                    </header>
                    {!isExpanded ? null : (
                      <div className="crm-arp-perm-group__grid">
                        {group.items.map((p) => {
                          const isActive = selectedPermIds.includes(Number(p.id));
                          return (
                            <label
                              key={p.id}
                              className={`crm-arp-perm-card${isActive ? ' is-active' : ''}`}
                            >
                              <div className="crm-arp-perm-card__copy">
                                <span className="crm-arp-perm-card__label">
                                  {formatPermissionLabel(p.code)}
                                </span>
                                <span className="crm-arp-perm-card__hint">{permissionHint(p.code)}</span>
                              </div>
                              <span className="crm-admin-rp-switch-control crm-arp-perm-card__switch">
                                <input
                                  className="crm-admin-rp-switch-input"
                                  type="checkbox"
                                  checked={isActive}
                                  onChange={() => togglePerm(Number(p.id))}
                                  disabled={!selectedRoleId || saving}
                                />
                                <span className="crm-admin-rp-switch-track" aria-hidden="true" />
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>

            <div className="crm-admin-rp-detail-foot crm-arp-perms-foot">
              {hasUnsavedPermChanges ? (
                <div className="crm-arp-perms-foot__hint">
                  <span className="crm-arp-perms-foot__dot" aria-hidden="true" />
                  Cambios sin guardar
                </div>
              ) : (
                <div className="crm-arp-perms-foot__hint crm-arp-perms-foot__hint--saved">
                  Permisos sincronizados con la base
                </div>
              )}
              <button
                type="button"
                className="crm-profile-primary-btn crm-admin-rp-save-btn crm-arp-perms-save"
                onClick={savePermissions}
                disabled={!selectedRoleId || saving || !hasUnsavedPermChanges}
              >
                {saving ? 'Guardando…' : 'Guardar permisos'}
              </button>
            </div>
          </section>
        ) : (
          <section className="crm-admin-users-form--empty" aria-label="Panel de edición vacío" style={{ alignContent: 'center' }}>
            <div className="crm-au-panel-empty">
              <div className="crm-au-panel-empty__icon" aria-hidden="true">
                <span className="crm-au-panel-empty__icon-ring" />
                <span className="crm-au-panel-empty__icon-glyph">
                  <UiIcon name="users" size={22} />
                </span>
              </div>
              <h3 className="crm-au-panel-empty__title">Sin rol seleccionado</h3>
              <p className="crm-au-panel-empty__copy">
                Elegí un rol de la lista para activar o desactivar permisos del menú y módulos.
              </p>
              <div className="crm-au-panel-empty__chips" aria-hidden="true">
                <span className="crm-au-panel-empty__chip">Datos y rol</span>
                <span className="crm-au-panel-empty__chip">Permisos</span>
              </div>
            </div>
          </section>
        )}
      </div>

      {isCreateRoleModalOpen ? (
        <div className="crm-admin-create-modal-backdrop">
          <div className="crm-admin-create-modal crm-arp-modal">
            <div className="crm-admin-create-modal__head">
              <div className="crm-admin-create-modal__title-row">
                <h3 className="crm-admin-create-modal__title">Crear nuevo rol</h3>
                <button type="button" className="crm-photo-studio-close-btn" onClick={() => setIsCreateRoleModalOpen(false)}>
                  Cerrar
                </button>
              </div>
              <div className="crm-admin-create-modal__notice">
                Ingresá el nombre del rol y presioná crear.
              </div>
            </div>
            <div className="crm-admin-create-modal__body">
              <input type="text" name="username" autoComplete="username" style={{ display: 'none' }} />
              <input type="password" name="password" autoComplete="new-password" style={{ display: 'none' }} />
              <input
                className="crm-admin-rp-create-input crm-profile-input"
                placeholder="Ej: supervisor"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                onFocus={() => {
                  if (String(newRoleName || '').trim().toLowerCase() === String(user?.username || '').trim().toLowerCase()) {
                    setNewRoleName('');
                  }
                }}
                autoComplete="new-password"
                name="new-role-name"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
              <button
                type="button"
                className="crm-profile-primary-btn crm-arp-save-btn"
                onClick={createRole}
                disabled={!canCreateRole}
                style={{
                  opacity: canCreateRole ? 1 : 0.55,
                  cursor: canCreateRole ? 'pointer' : 'not-allowed'
                }}
              >
                {saving ? 'Creando…' : 'Crear rol'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default AdminRolesPermisos;
